import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Activity, LayoutGrid, List, Plus, Presentation, Store, Target, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

import {
    AdminPage,
    ConfirmActionDialog,
    EmptyState,
    KpiCard,
    PillTabs,
    SearchField,
    formatNumber,
} from '../../../components/design-system/admin';
import { Vendor } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import { VendorsAsList } from '../../../modules/superadmin/our-vendors/components/vendors-as-list.component';
import { VendorsAsGrid } from '../../../modules/superadmin/our-vendors/components/vendor-as-grid.component';
import { VendorUsage } from '../../../modules/superadmin/our-vendors/components/vendor-row-parts.component';
import { ProspectsTable } from '../../../modules/superadmin/our-vendors/components/prospects-table.component';
import { useVendorsApi } from '../../../common/hooks/use-vendors-api.hook';
import { useDemoSitesApi } from '../../../common/hooks/use-demo-sites-api.hook';
import { useAdminOverviewApi } from '../../../common/hooks/use-admin-overview-api.hook';
import type { OverviewResponse } from '../../../common/models/admin-overview.model';

/** The usage columns and the last two KPIs cover this many days (the overview's range). */
const USAGE_DAYS = 30;

type ViewMode = 'list' | 'grid';
/** Customers, or the prospects the demo generator makes — listed apart. */
type Segment = 'customer' | 'prospect';

/**
 * Super-admin vendors: how many there are and how many are in use, then every
 * vendor with its plan, status, the last 30 days of usage and its credits.
 * The list is paged and searched server-side; usage comes from the admin
 * overview (one call) and is joined to the list by vendor id.
 */
export const OurVendors = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [stats, setStats] = useState<{ total: number; active: number; inactive: number } | null>(null);
    const [statsFailed, setStatsFailed] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [segment, setSegment] = useState<Segment>('customer');
    const [vendorToConvert, setVendorToConvert] = useState<Vendor | null>(null);
    const [vendorToMove, setVendorToMove] = useState<Vendor | null>(null);
    const [moving, setMoving] = useState(false);
    const [converting, setConverting] = useState(false);
    const [filter, setFilter] = useState('');
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [loading, setLoading] = useState(true);
    const [listFailed, setListFailed] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const [prevDebouncedFilter, setPrevDebouncedFilter] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [showDisableVendorDialog, setShowDisableVendorDialog] = useState(false);
    const [vendorToToggle, setVendorToToggle] = useState<Vendor | null>(null);
    const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [overview, setOverview] = useState<OverviewResponse | null>(null);
    const [overviewFailed, setOverviewFailed] = useState(false);
    const vendorsAPI = useVendorsApi()
    const demoSitesAPI = useDemoSitesApi();
    const [demoUrls, setDemoUrls] = useState<Record<string, string>>({});
    const overviewAPI = useAdminOverviewApi();
    const overviewApiRef = useRef(overviewAPI);
    overviewApiRef.current = overviewAPI;

    // Debounce the filter value
    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setDebouncedFilter(filter);
        }, 500);

        setSearchTimeout(timeout);

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter])

    // Main effect - fetch data when page, limit, or debounced filter changes.
    // An answer is used only while it is still the one asked for: a list that was still loading when the
    // tab (or page, or search) changed would otherwise land after the new one — the customers shown under
    // Prospects when the tab was clicked while they loaded.
    useEffect(() => {
        let stale = false;
        const fetchVendors = async () => {
            setLoading(true);
            try {
                // Check if this is a search change - if so, use page 1
                const isSearchChange = debouncedFilter !== prevDebouncedFilter;
                const pageToFetch = isSearchChange ? 1 : pagination.page;

                const response = await vendorsAPI.findAllVendors(
                    pageToFetch,
                    pagination.limit,
                    debouncedFilter || undefined,
                    segment,
                );
                if (stale) return;
                setVendors(response.data || []);
                setListFailed(false);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination?.total || 0,
                    totalPages: response.pagination?.totalPages || 0,
                    page: response.pagination?.page || pageToFetch
                }));

                // Update the previous filter after successful fetch
                if (isSearchChange) {
                    setPrevDebouncedFilter(debouncedFilter);
                }
            } catch (error) {
                if (stale) return;
                console.error('Error fetching vendors:', error);
                setListFailed(true);
            } finally {
                if (!stale) setLoading(false);
            }
        }

        fetchVendors();
        return () => { stale = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, pagination.limit, debouncedFilter, refreshTrigger, segment])

    // The prospects' demos: a link only for those that have one published (the latest, if several).
    useEffect(() => {
        if (segment !== 'prospect') return;
        let cancelled = false;
        demoSitesAPI.list()
            .then((sites) => {
                if (cancelled) return;
                const urls: Record<string, string> = {};
                for (const site of sites || []) {
                    const id = site.vendor?._id;
                    if (id && !urls[id]) urls[id] = site.url;
                }
                setDemoUrls(urls);
            })
            .catch((error) => console.error('Error fetching demo sites:', error));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segment, refreshTrigger])

    // Fetch aggregate stats (independent of pagination/search) so cards reflect the whole catalog
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await vendorsAPI.findVendorAdminStats();
                setStats({
                    total: response?.total ?? 0,
                    active: response?.active ?? 0,
                    inactive: response?.inactive ?? 0
                });
                setStatsFailed(false);
            } catch (error) {
                console.error('Error fetching vendor stats:', error);
                setStatsFailed(true);
            }
        };
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger])

    // Usage for the last 30 days, all vendors, once: plan, V2 products, visualizations, leads, last activity.
    useEffect(() => {
        let cancelled = false;
        overviewApiRef.current
            .getOverview(USAGE_DAYS)
            .then((o) => {
                if (cancelled) return;
                if (o?.vendors) setOverview(o);
                else setOverviewFailed(true);
            })
            .catch((error) => {
                console.error('Error fetching vendor usage:', error);
                if (!cancelled) setOverviewFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const usage: VendorUsage = useMemo(
        () => ({
            state: overview ? 'ready' : overviewFailed ? 'failed' : 'loading',
            byId: new Map((overview?.vendors || []).map((row) => [row.id, row])),
        }),
        [overview, overviewFailed],
    );

    const onDelete = (vendor: Vendor) => {
        setVendorToDelete(vendor);
    }

    const handleRemoveVendor = async(vendor: Vendor) => {
        await vendorsAPI.findOneVendorAndDelete(vendor._id)
        // Force refresh by incrementing refresh trigger
        setRefreshTrigger(prev => prev + 1);
    }

    const handleConfirmDelete = async () => {
        if (!vendorToDelete) return;
        setDeleting(true);
        try {
            await handleRemoveVendor(vendorToDelete);
            toast.success(t('ADMIN_VENDORS.TOAST.DELETED', { name: vendorToDelete.name }));
        } catch (error) {
            console.error('Error deleting vendor:', error);
            toast.error(t('ADMIN_VENDORS.TOAST.DELETE_FAILED'));
        } finally {
            setDeleting(false);
            setVendorToDelete(null);
        }
    }

    const handleSegmentChange = (next: Segment) => {
        if (next === segment) return;
        setVendors([]);
        setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
        setSegment(next);
    };

    const handleConfirmConvert = async () => {
        if (!vendorToConvert) return;
        setConverting(true);
        try {
            await vendorsAPI.convertProspect(vendorToConvert._id);
            toast.success(t('ADMIN_VENDORS.PROSPECTS.CONVERTED', { name: vendorToConvert.name }));
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Error converting prospect:', error);
            toast.error(t('ADMIN_VENDORS.PROSPECTS.CONVERT_FAILED'));
        } finally {
            setConverting(false);
            setVendorToConvert(null);
        }
    };

    const handleConfirmMove = async () => {
        if (!vendorToMove) return;
        setMoving(true);
        try {
            await vendorsAPI.moveToProspects(vendorToMove._id);
            toast.success(t('ADMIN_VENDORS.PROSPECTS.MOVED', { name: vendorToMove.name }));
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Error moving vendor to prospects:', error);
            toast.error(t('ADMIN_VENDORS.PROSPECTS.MOVE_FAILED'));
        } finally {
            setMoving(false);
            setVendorToMove(null);
        }
    };

    const handleFilterChange = (value: string) => {
        setFilter(value);
        // Page reset is handled in the search effect
    };

    const handlePageChange = (newPage: number) => {
        setPagination(prev => ({ ...prev, page: newPage + 1 })); // List component uses 0-based indexing
    };

    const handleRowsPerPageChange = (newLimit: number) => {
        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    };

    const handleEditVendor = (vendor: Vendor) => {
        navigate(`/vendors/${vendor._id}/edit`)
    }

    const handleToggleVendorStatus = (vendor: Vendor) => {
        const willBeDisabled = vendor.isEnabled !== false;

        // Only show confirmation when disabling a vendor
        if (willBeDisabled) {
            setVendorToToggle(vendor);
            setShowDisableVendorDialog(true);
        } else {
            // Enable without confirmation
            performToggleVendorStatus(vendor);
        }
    }

    const handleConfirmDisableVendor = () => {
        if (vendorToToggle) {
            performToggleVendorStatus(vendorToToggle);
        }
        setShowDisableVendorDialog(false);
        setVendorToToggle(null);
    }

    const handleCancelDisableVendor = () => {
        setShowDisableVendorDialog(false);
        setVendorToToggle(null);
    }

    const performToggleVendorStatus = async (vendor: Vendor) => {
        try {
            const newStatus = vendor.isEnabled !== false ? false : true;
            // Only send the changed field
            await vendorsAPI.updateVendor(vendor._id, { isEnabled: newStatus });
            toast.success(t(newStatus ? 'ADMIN_VENDORS.TOAST.ENABLED' : 'ADMIN_VENDORS.TOAST.DISABLED', { name: vendor.name }));
            // Force refresh by incrementing refresh trigger
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Error toggling vendor status:', error);
            toast.error(t('ADMIN_VENDORS.TOAST.STATUS_FAILED'));
        }
    }

    const toggleViewMode = () => {
        setViewMode((prevMode) => (prevMode === 'list' ? 'grid' : 'list'));
    };

    const handleAddNewVendor = () => {
        navigate('/vendors/new');
    };

    const k = overview?.kpis;
    const vsPrevious = t('ADMIN_SHELL.COMMON.VS_PREVIOUS', { days: USAGE_DAYS });
    const overviewLoading = !overview && !overviewFailed;
    const empty = !loading && vendors.length === 0;

    const collectionProps = {
        vendors,
        usage,
        pagination,
        loading,
        handleEditVendor,
        onDelete,
        onToggleStatus: handleToggleVendorStatus,
        onMoveToProspects: setVendorToMove,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
    };

    return (
        <AdminPage
            title={t('ADMIN_SHELL.NAV.VENDORS')}
            description={t('ADMIN_VENDORS.DESCRIPTION')}
            actions={
                <Button onClick={handleAddNewVendor}>
                    <Plus className="size-4" />
                    {t('ADMIN_VENDORS.ADD')}
                </Button>
            }
        >
            <PillTabs<Segment>
                value={segment}
                onChange={handleSegmentChange}
                ariaLabel={t('ADMIN_VENDORS.SEGMENT.LABEL')}
                options={[
                    { value: 'customer', label: t('ADMIN_VENDORS.SEGMENT.CUSTOMERS'), icon: <Store /> },
                    { value: 'prospect', label: t('ADMIN_VENDORS.SEGMENT.PROSPECTS'), icon: <Presentation /> },
                ]}
            />

            {segment === 'customer' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="vendors-kpis">
                <KpiCard
                    label={t('ADMIN_VENDORS.KPI.TOTAL')}
                    value={stats ? formatNumber(stats.total) : '—'}
                    hint={stats ? t('ADMIN_VENDORS.KPI.TOTAL_HINT', { enabled: formatNumber(stats.active), disabled: formatNumber(stats.inactive) }) : statsFailed ? t('ADMIN_VENDORS.KPI.UNAVAILABLE') : undefined}
                    icon={<Store />}
                    loading={!stats && !statsFailed}
                    testId="kpi-total-vendors"
                />
                <KpiCard
                    label={t('ADMIN_VENDORS.KPI.ACTIVE')}
                    value={k ? formatNumber(k.vendors.active) : '—'}
                    delta={k ? { current: k.vendors.active, previous: k.vendors.activePrevious } : undefined}
                    hint={k ? t('ADMIN_VENDORS.KPI.ACTIVE_HINT', { enabled: formatNumber(k.vendors.enabled) }) : overviewFailed ? t('ADMIN_VENDORS.KPI.UNAVAILABLE') : undefined}
                    icon={<Activity />}
                    loading={overviewLoading}
                    testId="kpi-active-vendors"
                />
                <KpiCard
                    label={t('ADMIN_VENDORS.KPI.VISUALIZATIONS')}
                    value={k ? formatNumber(k.visualizations.current) : '—'}
                    delta={k?.visualizations}
                    hint={k ? vsPrevious : overviewFailed ? t('ADMIN_VENDORS.KPI.UNAVAILABLE') : undefined}
                    icon={<Zap />}
                    trend={overview?.series?.map((p) => p.visualizations)}
                    loading={overviewLoading}
                    testId="kpi-visualizations"
                />
                <KpiCard
                    label={t('ADMIN_VENDORS.KPI.LEADS')}
                    value={k ? formatNumber(k.leads.current) : '—'}
                    delta={k?.leads}
                    hint={k ? vsPrevious : overviewFailed ? t('ADMIN_VENDORS.KPI.UNAVAILABLE') : undefined}
                    icon={<Target />}
                    loading={overviewLoading}
                    testId="kpi-leads"
                />
            </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <SearchField
                    value={filter}
                    onChange={handleFilterChange}
                    placeholder={t('ADMIN_VENDORS.SEARCH_PLACEHOLDER')}
                />
                {segment === 'customer' && (
                <PillTabs<ViewMode>
                    value={viewMode}
                    onChange={(v) => v !== viewMode && toggleViewMode()}
                    ariaLabel={t('ADMIN_VENDORS.VIEW_LABEL')}
                    options={[
                        { value: 'list', label: t('ADMIN_SHELL.COMMON.LIST'), icon: <List /> },
                        { value: 'grid', label: t('ADMIN_SHELL.COMMON.GRID'), icon: <LayoutGrid /> },
                    ]}
                />
                )}
            </div>

            {empty ? (
                <Card className="py-0" data-testid="vendors-empty">
                    {listFailed ? (
                        <EmptyState
                            title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                            action={
                                <Button variant="outline" onClick={() => setRefreshTrigger(prev => prev + 1)}>
                                    {t('ADMIN_SHELL.COMMON.RETRY')}
                                </Button>
                            }
                            className="py-16"
                        />
                    ) : debouncedFilter ? (
                        <EmptyState
                            icon={<Store />}
                            title={t('ADMIN_VENDORS.EMPTY.NO_MATCH_TITLE', { query: debouncedFilter })}
                            description={t('ADMIN_VENDORS.EMPTY.NO_MATCH_BODY')}
                            action={
                                <Button variant="outline" onClick={() => handleFilterChange('')}>
                                    {t('ADMIN_SHELL.COMMON.CLEAR')}
                                </Button>
                            }
                            className="py-16"
                        />
                    ) : segment === 'prospect' ? (
                        <EmptyState
                            icon={<Presentation />}
                            title={t('ADMIN_VENDORS.PROSPECTS.EMPTY_TITLE')}
                            description={t('ADMIN_VENDORS.PROSPECTS.EMPTY_BODY')}
                            action={
                                <Button variant="outline" onClick={() => navigate('/demo-generator')}>
                                    {t('ADMIN_VENDORS.PROSPECTS.OPEN_GENERATOR')}
                                </Button>
                            }
                            className="py-16"
                        />
                    ) : (
                        <EmptyState
                            icon={<Store />}
                            title={t('ADMIN_VENDORS.EMPTY.TITLE')}
                            description={t('ADMIN_VENDORS.EMPTY.BODY')}
                            action={
                                <Button onClick={handleAddNewVendor}>
                                    <Plus className="size-4" />
                                    {t('ADMIN_VENDORS.ADD')}
                                </Button>
                            }
                            className="py-16"
                        />
                    )}
                </Card>
            ) : (
                <div key={`${segment}-${viewMode}`} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {segment === 'prospect' ? (
                        <ProspectsTable
                            vendors={vendors}
                            demoUrls={demoUrls}
                            loading={loading}
                            pagination={pagination}
                            onEdit={handleEditVendor}
                            onConvert={setVendorToConvert}
                            onToggleStatus={handleToggleVendorStatus}
                            onDelete={onDelete}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                        />
                    ) : viewMode === 'list' ? <VendorsAsList {...collectionProps} /> : <VendorsAsGrid {...collectionProps} />}
                </div>
            )}

            <ConfirmActionDialog
                open={!!vendorToDelete}
                onOpenChange={(open) => !open && setVendorToDelete(null)}
                title={t('ADMIN_VENDORS.CONFIRM.DELETE_TITLE', { name: vendorToDelete?.name })}
                description={t('ADMIN_VENDORS.CONFIRM.DELETE_BODY', { name: vendorToDelete?.name })}
                confirmLabel={t('ADMIN_VENDORS.CONFIRM.DELETE_CONFIRM')}
                destructive
                busy={deleting}
                onConfirm={handleConfirmDelete}
            />

            <ConfirmActionDialog
                open={!!vendorToMove}
                onOpenChange={(open) => !open && setVendorToMove(null)}
                title={t('ADMIN_VENDORS.PROSPECTS.MOVE_TITLE', { name: vendorToMove?.name })}
                description={t('ADMIN_VENDORS.PROSPECTS.MOVE_BODY', { name: vendorToMove?.name })}
                confirmLabel={t('ADMIN_VENDORS.MENU.MOVE_TO_PROSPECTS')}
                busy={moving}
                onConfirm={handleConfirmMove}
            />

            <ConfirmActionDialog
                open={!!vendorToConvert}
                onOpenChange={(open) => !open && setVendorToConvert(null)}
                title={t('ADMIN_VENDORS.PROSPECTS.CONVERT_TITLE', { name: vendorToConvert?.name })}
                description={t('ADMIN_VENDORS.PROSPECTS.CONVERT_BODY', { name: vendorToConvert?.name })}
                confirmLabel={t('ADMIN_VENDORS.PROSPECTS.CONVERT')}
                busy={converting}
                onConfirm={handleConfirmConvert}
            />

            <ConfirmActionDialog
                open={showDisableVendorDialog}
                onOpenChange={(open) => !open && handleCancelDisableVendor()}
                title={t('ADMIN_VENDORS.CONFIRM.DISABLE_TITLE', { name: vendorToToggle?.name })}
                description={t('ADMIN_VENDORS.CONFIRM.DISABLE_BODY', { name: vendorToToggle?.name })}
                confirmLabel={t('ADMIN_VENDORS.CONFIRM.DISABLE_CONFIRM')}
                destructive
                onConfirm={handleConfirmDisableVendor}
            />
        </AdminPage>
    );
};

export default OurVendors;
